import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Dropdown from "react-bootstrap/Dropdown";
import DropdownButton from "react-bootstrap/DropdownButton";
import { Modal } from "react-bootstrap";
import Select from "react-select";
import { useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import {
  assignJsonFromSelectOptions,
  getAccessionPlaceholderForWell96,
  groupTestControlOptions,
  type PlateAssignOption,
  type PlateAssignGroup,
  type PlateSlot,
  selectValuesFromAssignJson,
  serializeAssignTestJsonForDb,
  slotsFromGeneratedDetails,
} from "./plateSetup96";
import {
  CREATE_PLATE_TEMPLATES_PERMISSION,
  ID_LIS_MODULE_NAME,
  PLATE_FILTER_FIELDS,
  PLATE_FILTER_LABELS,
  PLATE_SETUP_PERMISSION_PAGE,
  accessionsForPlateType,
  accessionSelectOptions,
  createId,
  fullWidthSelectStyles,
  initialSearch,
  inputStyles,
  parsePanelIds,
  serializePanelIds,
  type LookupOption,
  type PlateSetupRecord,
} from "./plateSetupHelpers";
import BreadCrumbs from "Utils/Common/Breadcrumb";
import { ArrowDown, ArrowUp, CrossIcon, DoneIcon } from "Shared/Icons";
import useLang from "Shared/hooks/useLanguage";
import usePagination from "Shared/hooks/usePagination";
import CustomPagination from "Shared/JsxPagination";
import { sortById, SortingTypeI } from "Utils/consts";
import { styles } from "Utils/Common";
import PermissionComponent, {
  getPermissionDisplayName,
} from "Shared/Common/Permissions/PermissionComponent";
import PlateSetupService from "Services/InfectiousDisease/PlateSetupService";

export type {
  AssignTestJsonV1,
  PlateSetupDetailRow,
  PlateSlot,
} from "./plateSetup96";

const getPanelLabels = (
  options: LookupOption[],
  panelIds?: string | null
): string => {
  if (!panelIds) return "";
  const selectedIds = parsePanelIds(panelIds);
  return selectedIds
    .map(
      (panelId) =>
        options.find((option) => option.value === panelId)?.label ?? panelId
    )
    .filter(Boolean)
    .join(", ");
};

const PLATE_SETUP_DETAILS_QUERY_KEY = "plate-setup-details";

const PlateSetup: React.FC = () => {
  const { t } = useLang();
  const queryClient = useQueryClient();

  // ── Lookups (unchanged) ────────────────────────────────────────────────────
  const {
    data: plateSetupLookupsResponse,
    isLoading: isLookupsLoading,
    isError: isLookupsError,
    error: lookupsError,
  } = useQuery("plate-setup-lookups", PlateSetupService.GetPlateSetupLookups, {
    refetchOnWindowFocus: false,
    onError: (error: any) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Unable to load plate setup lookups";
      toast.error(t(message));
    },
  });

  const plateTypeOptions: LookupOption[] = useMemo(
    () => plateSetupLookupsResponse?.data?.PlateType ?? [],
    [plateSetupLookupsResponse]
  );
  const mappingTypeOptions: LookupOption[] = useMemo(
    () => plateSetupLookupsResponse?.data?.MappingType ?? [],
    [plateSetupLookupsResponse]
  );
  const panelOptions: LookupOption[] = useMemo(
    () => plateSetupLookupsResponse?.data?.Panel ?? [],
    [plateSetupLookupsResponse]
  );

  // ── Helpers ────────────────────────────────────────────────────────────────
  const getLookupLabel = (
    options: LookupOption[],
    value?: string | number | null
  ): string => {
    if (value === null || value === undefined || value === "") return "";
    return (
      options.find((o) => o.value === String(value))?.label ?? String(value)
    );
  };

  const mapApiRowToPlateSetupRecord = useCallback(
    (
      apiRow: import("Services/InfectiousDisease/PlateSetupService").PlateSetupListResponse
    ): PlateSetupRecord => ({
      id: String(apiRow.plateSetupId),
      plateSetupId: apiRow.plateSetupId,
      rowStatus: false,
      // testType: apiRow.runName ?? "",
      panelId: apiRow.panelId ?? null,
      panelName: apiRow.panelName ?? null,
      panel: apiRow.panelName ?? getPanelLabels(panelOptions, apiRow.panelId),
      templateId: null,
      template: apiRow.outputTemplate ?? "",
      mappingTypeId: apiRow.mappingTypeId ?? null,
      mappingType: getLookupLabel(mappingTypeOptions, apiRow.mappingTypeId),
      plateTypeId: apiRow.plateTypeId ?? "1",
      plateType:
        getLookupLabel(plateTypeOptions, apiRow.plateTypeId) ||
        apiRow.plateTypeId ||
        "96 Well",
      accessionsPerPlate: Number(apiRow.noOfAccessionId ?? 0),
      slots: [],
    }),
    [panelOptions, mappingTypeOptions, plateTypeOptions]
  );

  // ── State ──────────────────────────────────────────────────────────────────
  const [rows, setRows] = useState<PlateSetupRecord[]>([]);
  const [isSetupsLoading, setIsSetupsLoading] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const expandedPlateSetupId = useMemo(
    () => rows.find((row) => row.id === expandedId)?.plateSetupId,
    [expandedId, rows]
  );

  const {
    data: expandedPlateSetupDetails,
    isLoading: isPlateDetailsLoading,
    isError: isPlateDetailsError,
    error: plateDetailsError,
    refetch: refetchPlateDetails,
  } = useQuery(
    [PLATE_SETUP_DETAILS_QUERY_KEY, expandedPlateSetupId],
    async (): Promise<
      import("Services/InfectiousDisease/PlateSetupService").PlateSetupDetailsResponse
    > => {
      const response = await PlateSetupService.GetPlateSetupDetails(
        expandedPlateSetupId!
      );
      const body = response?.data;
      const statusCode = Number(body?.statusCode);

      if (!statusCode || statusCode < 200 || statusCode >= 300 || !body?.data) {
        throw new Error(body?.message || "Unable to load plate setup details");
      }

      return body.data;
    },
    {
      enabled: Boolean(expandedPlateSetupId),
      staleTime: Infinity,
      cacheTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    }
  );

  useEffect(() => {
    if (!expandedId || !expandedPlateSetupDetails) return;

    setRows((currentRows) =>
      currentRows.map((row) => {
        if (row.id !== expandedId || row.slots.length > 0) return row;

        return {
          ...row,
          slots: slotsFromGeneratedDetails(
            expandedPlateSetupDetails.wells ?? []
          ),
        };
      })
    );
  }, [expandedId, expandedPlateSetupDetails]);

  // Cache of test/control options keyed by the normalized panel ID list.
  // Fetched lazily the first time a row with those panels is expanded.
  const [testControlCache, setTestControlCache] = useState<
    Record<string, PlateAssignOption[]>
  >({});
  const [testControlLoading, setTestControlLoading] = useState<
    Record<string, boolean>
  >({});

  const fetchTestControlOptions = useCallback(
    async (panelIds: string) => {
      const key = panelIds.trim();
      if (!key) return;
      if (testControlCache[key] !== undefined) return; // already cached
      setTestControlLoading((prev) => ({ ...prev, [key]: true }));
      try {
        const res = await PlateSetupService.GetTestControlLookups(key);
        const options: PlateAssignOption[] = res?.data ?? [];
        setTestControlCache((prev) => ({ ...prev, [key]: options }));
      } catch {
        setTestControlCache((prev) => ({ ...prev, [key]: [] }));
        toast.error(t("Unable to load test/control options for this panel"));
      } finally {
        setTestControlLoading((prev) => ({ ...prev, [key]: false }));
      }
    },
    [testControlCache, t]
  );
  const [searchRequest, setSearchRequest] = useState(initialSearch);
  const [searchedTags, setSearchedTags] = useState<string[]>([]);
  const [sort, setSorting] = useState<SortingTypeI>(sortById);
  const [isAddButtonDisabled, setIsAddButtonDisabled] = useState(false);

  // ── Pagination ─────────────────────────────────────────────────────────────
  const {
    curPage,
    showPage,
    nextPage,
    prevPage,
    pageSize,
    total,
    totalPages,
    pageNumbers,
    setPageSize,
    setTotal,
    setCurPage,
  } = usePagination();

  // keep a ref to the active search values so loadData always reads the latest
  const searchRef = useRef(searchRequest);
  useEffect(() => {
    searchRef.current = searchRequest;
  }, [searchRequest]);

  useEffect(() => {
    const tags = PLATE_FILTER_FIELDS.filter((field) =>
      String(searchRequest[field] ?? "").trim()
    ).map((field) => t(PLATE_FILTER_LABELS[field]));
    setSearchedTags(tags);
  }, [searchRequest, t]);

  // ── Server fetch ───────────────────────────────────────────────────────────
  const loadData = useCallback(
    async (
      page: number,
      size: number,
      sortState: SortingTypeI,
      query: typeof initialSearch,
      preserveDrafts = true
    ) => {
      setIsSetupsLoading(true);
      try {
        const payload: import("Services/InfectiousDisease/PlateSetupService").PlateSetupSearchRequest =
          {
            pageNumber: page,
            pageSize: size,
            sortColumn: sortState.clickedIconData ?? null,
            sortDirection: sortState.sortingOrder ?? null,
            queryModel: {
              // runName: query.runName.trim() || null,
              panelId: query.panelId || null,
              outputTemplate: query.outputTemplate.trim() || null,
              mappingTypeId: query.mappingTypeId || null,
              plateTypeId: query.plateTypeId || null,
              noOfAccessionId: query.noOfAccessionId.trim() || null,
            },
          };
        const res = await PlateSetupService.SearchPlateSetups(payload);
        // backend returns { statusCode, total, data } directly at the response body level
        const body = res?.data as any;
        const apiRows = body?.data ?? [];
        const serverTotal = body?.total ?? 0;
        setTotal(serverTotal);
        const mappedApiRows = apiRows.map(
          (
            apiRow: import("Services/InfectiousDisease/PlateSetupService").PlateSetupListResponse
          ) => {
            const mappedRow = mapApiRowToPlateSetupRecord(apiRow);
            const cachedDetails = queryClient.getQueryData<
              import("Services/InfectiousDisease/PlateSetupService").PlateSetupDetailsResponse
            >([PLATE_SETUP_DETAILS_QUERY_KEY, apiRow.plateSetupId]);

            return cachedDetails
              ? {
                  ...mappedRow,
                  slots: slotsFromGeneratedDetails(cachedDetails.wells ?? []),
                }
              : mappedRow;
          }
        );

        setRows((prev) => {
          const drafts = preserveDrafts
            ? prev.filter((r) => r.rowStatus === true)
            : [];
          return [...drafts, ...mappedApiRows];
        });
      } catch (error: any) {
        toast.error(
          t(
            error?.response?.data?.message ||
              error?.message ||
              "Unable to load plate setups"
          )
        );
      } finally {
        setIsSetupsLoading(false);
      }
    },
    [mapApiRowToPlateSetupRecord, queryClient, t]
  );

  // initial load
  useEffect(() => {
    loadData(curPage, pageSize, sort, searchRequest);
  }, []);

  // re-fetch when page or page-size changes (but not on first render — handled above)
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    loadData(curPage, pageSize, sort, searchRef.current);
  }, [curPage, pageSize]);

  const handleSort = (columnName: string) => {
    setSorting((prev) => {
      const sameColumn = prev.clickedIconData === columnName;
      const nextOrder =
        !sameColumn || !prev.sortingOrder
          ? "asc"
          : prev.sortingOrder === "asc"
            ? "desc"
            : "asc";
      const next = { sortingOrder: nextOrder, clickedIconData: columnName };
      loadData(1, pageSize, next, searchRef.current);
      setCurPage(1);
      return next;
    });
  };

  const onSearchQueryChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setSearchRequest((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = () => {
    setCurPage(1);
    loadData(1, pageSize, sort, searchRef.current);
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleTagRemoval = (clickedTag: string) => {
    const field = PLATE_FILTER_FIELDS.find(
      (f) => t(PLATE_FILTER_LABELS[f]) === clickedTag
    );
    if (!field) return;
    const next = { ...searchRequest, [field]: "" };
    setSearchRequest(next);
    searchRef.current = next;
    setCurPage(1);
    loadData(1, pageSize, sort, next);
  };

  const handleReset = () => {
    const cleared = { ...initialSearch };
    setSearchRequest(cleared);
    searchRef.current = cleared;
    setSorting(sortById);
    setPageSize(50);
    setCurPage(1);
    setIsAddButtonDisabled(false);
    setRows((prev) => prev.filter((r) => !r.rowStatus));
    loadData(1, 50, sortById, cleared);
  };

  const updateRow = (id: string, patch: Partial<PlateSetupRecord>) => {
    setRows((curr) =>
      curr.map((r) => {
        if (r.id !== id) return r;
        let next = { ...r, ...patch };
        if (patch.plateType !== undefined || patch.plateTypeId !== undefined) {
          const opts = accessionsForPlateType(next.plateTypeId, next.plateType);
          if (!opts.includes(next.accessionsPerPlate)) {
            next = { ...next, accessionsPerPlate: opts[0] };
          }
        }
        return next;
      })
    );
  };

  const updateSlot = (
    rowId: string,
    slotIndex: number,
    patch: Partial<PlateSlot>
  ) => {
    setRows((curr) =>
      curr.map((r) => {
        if (r.id !== rowId) return r;
        const slots = r.slots.map((s, i) =>
          i === slotIndex ? { ...s, ...patch } : s
        );
        return { ...r, slots };
      })
    );
  };

  const confirmNewRow = async (row: PlateSetupRecord) => {
    try {
      if (!row.template || !row.template.trim()) {
        toast.error(t("Template is required"));
        return;
      }
      if (!row.panelId?.trim()) {
        toast.error(t("At least one panel is required"));
        return;
      }
      const response = await PlateSetupService.GeneratePlateSetupStructure({
        panelId: row.panelId,
        // runName: row.testType || null,
        mappingTypeId: row.mappingTypeId ? String(row.mappingTypeId) : null,
        plateTypeId: String(row.plateTypeId),
        noOfAccessionsInOnePlate: String(row.accessionsPerPlate),
        outputTemplate: row.template,
        createdBy: null,
      });

      const responseData = response?.data;
      const statusCode = (responseData as any)?.statusCode;
      const isFailureStatus =
        statusCode !== undefined &&
        statusCode !== null &&
        statusCode !== 200 &&
        statusCode !== "200" &&
        statusCode !== "OK";

      if (isFailureStatus) {
        throw new Error(
          (responseData as any)?.message ||
            "Unable to generate plate setup structure"
        );
      }

      setExpandedId(null);
      setIsAddButtonDisabled(false);
      await loadData(curPage, pageSize, sort, searchRef.current, false);
      toast.success(
        t((responseData as any)?.message || "Request Successfully Processed")
      );
    } catch (error: any) {
      toast.error(
        t(
          error?.response?.data?.message ||
            error?.message ||
            "Unable to generate plate setup structure"
        )
      );
    }
  };

  const cancelNewRow = (id: string) => {
    setRows((curr) => curr.filter((r) => r.id !== id));
    setIsAddButtonDisabled(false);
  };

  // ── Save Assignments ────────────────────────────────────────────────────────
  const [isSavingAssignments, setIsSavingAssignments] = useState<
    Record<string, boolean>
  >({});

  const saveAssignments = async (row: PlateSetupRecord) => {
    if (!row.plateSetupId) {
      toast.error(t("Cannot save — plate setup ID is missing"));
      return;
    }
    setIsSavingAssignments((prev) => ({ ...prev, [row.id]: true }));
    try {
      const wells = row.slots
        .filter((s) => s.plateSetupDetailId !== undefined)
        .map((s) => ({
          plateSetupDetailId: s.plateSetupDetailId!,
          assignTestJson: s.assignTestJson?.entries?.length
            ? serializeAssignTestJsonForDb(s.assignTestJson)
            : null,
        }));

      const res = await PlateSetupService.UpdateStructureAssignments({
        plateSetupId: row.plateSetupId,
        wells,
      });

      const body = res?.data as any;
      if (body?.statusCode && body.statusCode !== 200) {
        throw new Error(body.message || "Failed to save assignments");
      }
      await queryClient.invalidateQueries([
        PLATE_SETUP_DETAILS_QUERY_KEY,
        row.plateSetupId,
      ]);
      toast.success(t(body?.message || "Assignments saved successfully"));
    } catch (error: any) {
      toast.error(
        t(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to save assignments"
        )
      );
    } finally {
      setIsSavingAssignments((prev) => ({ ...prev, [row.id]: false }));
    }
  };

  // ── Delete Plate Setup ──────────────────────────────────────────────────────
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [isDeletingId, setIsDeletingId] = useState<string | null>(null);

  const handleDeleteConfirm = async () => {
    const row = rows.find((r) => r.id === deleteConfirmId);
    if (!row?.plateSetupId) return;
    setIsDeletingId(row.id);
    setDeleteConfirmId(null);
    try {
      const res = await PlateSetupService.DeletePlateSetup(row.plateSetupId);
      const body = res?.data as any;
      if (body?.statusCode && body.statusCode !== 200) {
        throw new Error(body.message || "Failed to delete plate setup");
      }
      toast.success(t(body?.message || "Plate setup deleted successfully"));
      queryClient.removeQueries([
        PLATE_SETUP_DETAILS_QUERY_KEY,
        row.plateSetupId,
      ]);
      if (expandedId === row.id) setExpandedId(null);
      loadData(curPage, pageSize, sort, searchRef.current);
    } catch (error: any) {
      toast.error(
        t(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to delete plate setup"
        )
      );
    } finally {
      setIsDeletingId(null);
    }
  };

  const renderSortHeader = (label: string, column: string) => (
    <div
      onClick={() => handleSort(column)}
      className="d-flex justify-content-between cursor-pointer"
    >
      <div style={{ width: "max-content" }}>{t(label)}</div>
      <div className="d-flex justify-content-center align-items-center mx-4 mr-0">
        <ArrowUp
          CustomeClass={`${
            sort.sortingOrder === "desc" && sort.clickedIconData === column
              ? "text-success fs-7"
              : "text-gray-700 fs-7"
          } p-0 m-0`}
        />
        <ArrowDown
          CustomeClass={`${
            sort.sortingOrder === "asc" && sort.clickedIconData === column
              ? "text-success fs-7"
              : "text-gray-700 fs-7"
          } p-0 m-0`}
        />
      </div>
    </div>
  );

  return (
    <>
      <div className="app-toolbar py-2 py-lg-3">
        <div className="app-container container-fluid d-flex flex-wrap gap-4 justify-content-center justify-content-sm-between align-items-center">
          <BreadCrumbs />
        </div>
      </div>
      <div className="d-flex flex-column flex-column-fluid">
        <div className="app-content flex-column-fluid">
          <div className="app-container container-fluid">
            <div className="d-flex flex-wrap justify-content-center justify-content-sm-between align-items-center responsive-flexed-actions mb-2 gap-2">
              <div className="d-flex align-items-center responsive-flexed-actions gap-2">
                <div className="d-flex align-items-center">
                  <span className="fw-400 mr-3">{t("Records")}</span>
                  <select
                    id="PlateSetupRecordsPageSize"
                    className="form-select w-100px h-33px rounded"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(parseInt(e.target.value, 10));
                      setCurPage(1);
                    }}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
                <PermissionComponent
                  moduleName={ID_LIS_MODULE_NAME}
                  pageName={PLATE_SETUP_PERMISSION_PAGE}
                  permissionIdentifier={CREATE_PLATE_TEMPLATES_PERMISSION}
                >
                  {({ pageName, moduleName, permissionIdentifier }) => (
                    <button
                      id="PlateSetupAddNew"
                      type="button"
                      className="btn btn-primary btn-sm fw-bold mr-3 px-10 text-capitalize"
                      disabled={
                        isAddButtonDisabled ||
                        isLookupsLoading ||
                        isSetupsLoading
                      }
                      onClick={() => {
                        if (!isAddButtonDisabled) {
                          const defaultMapping = mappingTypeOptions[0];
                          const defaultPlateType = plateTypeOptions[0];
                          setRows((prev) => [
                            {
                              id: createId(),
                              rowStatus: true,
                              // testType: "Infectious Disease",
                              // panelId: defaultPanel?.value
                              //   ? Number(defaultPanel.value)
                              //   : null,
                              panelId: null,
                              panel: "",
                              templateId: null,
                              template: "",
                              mappingTypeId: defaultMapping?.value ?? null,
                              mappingType: defaultMapping?.label ?? "",
                              plateTypeId: defaultPlateType?.value ?? "1",
                              plateType: defaultPlateType?.label ?? "96 Well",
                              accessionsPerPlate: 2,
                              slots: [],
                            },
                            ...prev,
                          ]);
                          setIsAddButtonDisabled(true);
                        }
                      }}
                    >
                      {isLookupsLoading || isSetupsLoading
                        ? t("Loading...")
                        : getPermissionDisplayName(
                            pageName,
                            moduleName,
                            permissionIdentifier,
                            "Add New"
                          )}
                    </button>
                  )}
                </PermissionComponent>
              </div>
              <div className="d-flex align-items-center gap-2">
                <button
                  id="PlateSetupSearch"
                  type="button"
                  className="btn btn-primary btn-sm fw-bold px-10 text-capitalize"
                  onClick={handleSearch}
                >
                  {t("Search")}
                </button>
                <button
                  id="PlateSetupReset"
                  type="button"
                  className="btn btn-secondary btn-sm fw-bold px-10 text-capitalize"
                  onClick={handleReset}
                >
                  {t("Reset")}
                </button>
              </div>
            </div>

            <div className="card">
              <div className="card-body py-2">
                {isLookupsError && (
                  <div className="text-danger px-4 pt-3">
                    {t(
                      (lookupsError as any)?.response?.data?.message ||
                        (lookupsError as any)?.message ||
                        "Unable to load plate setup lookups"
                    )}
                  </div>
                )}
                <div className="d-flex gap-2 flex-wrap pb-2">
                  {searchedTags.map((tag) => (
                    <div
                      key={tag}
                      className="d-flex align-items-center cursor-pointer gap-1 p-2 rounded bg-light"
                      onClick={() => handleTagRemoval(tag)}
                    >
                      <span className="fw-bold">{tag}</span>
                      <i className="bi bi-x" />
                    </div>
                  ))}
                </div>
                <Box sx={{ height: "auto", width: "100%" }}>
                  <div className="table_bordered overflow-hidden">
                    <TableContainer
                      sx={{
                        maxHeight: "calc(100vh - 100px)",
                        "&::-webkit-scrollbar": { width: 7 },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "#fff",
                        },
                        "&:hover": {
                          "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "var(--kt-gray-400)",
                            borderRadius: 2,
                          },
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "var(--kt-gray-400)",
                          borderRadius: 2,
                        },
                      }}
                      component={Paper}
                      className="shadow-none"
                    >
                      <Table
                        aria-label="plate setup"
                        className="table table-cutome-expend table-bordered table-sticky-header table-head-2-bg table-bg table-head-custom table-vertical-center border-0 mb-0"
                      >
                        <TableHead>
                          <TableRow className="h-40px">
                            <TableCell />
                            <TableCell />
                            {/* <TableCell>
                            <input
                              id="PlateSetupSearchTestType"
                              type="text"
                              name="runName"
                              className="form-control bg-white min-w-100px w-100 rounded-2 fs-8 h-30px"
                              placeholder={t("Search ...")}
                              value={searchRequest.runName}
                              onChange={onSearchQueryChange}
                              onKeyDown={handleSearchKeyPress}
                            />
                          </TableCell> */}
                            <TableCell
                              className="min-w-150px w-150px"
                              sx={{ width: "max-content" }}
                            >
                              <Select
                                inputId="PlateSetupSearchPanel"
                                theme={(theme) => styles(theme)}
                                options={panelOptions}
                                styles={fullWidthSelectStyles}
                                menuPortalTarget={document.body}
                                placeholder={t("Select...")}
                                isSearchable={true}
                                isClearable={true}
                                className="w-100"
                                value={
                                  panelOptions.find(
                                    (o) => o.value === searchRequest.panelId
                                  ) ?? null
                                }
                                onChange={(opt: any) =>
                                  setSearchRequest((prev) => ({
                                    ...prev,
                                    panelId: opt?.value ?? "",
                                  }))
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <input
                                id="PlateSetupSearchTemplate"
                                type="text"
                                name="outputTemplate"
                                className="form-control bg-white min-w-100px w-100 rounded-2 fs-8 h-30px"
                                placeholder={t("Search ...")}
                                value={searchRequest.outputTemplate}
                                onChange={onSearchQueryChange}
                                onKeyDown={handleSearchKeyPress}
                              />
                            </TableCell>
                            <TableCell>
                              <Select
                                inputId="PlateSetupSearchMapping"
                                theme={(theme) => styles(theme)}
                                options={mappingTypeOptions}
                                styles={fullWidthSelectStyles}
                                menuPortalTarget={document.body}
                                placeholder={t("Select...")}
                                isSearchable={true}
                                isClearable={true}
                                className="w-100"
                                value={
                                  mappingTypeOptions.find(
                                    (o) =>
                                      o.value === searchRequest.mappingTypeId
                                  ) ?? null
                                }
                                onChange={(opt: any) =>
                                  setSearchRequest((prev) => ({
                                    ...prev,
                                    mappingTypeId: opt?.value ?? "",
                                  }))
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <Select
                                inputId="PlateSetupSearchPlateType"
                                theme={(theme) => styles(theme)}
                                options={plateTypeOptions}
                                styles={fullWidthSelectStyles}
                                menuPortalTarget={document.body}
                                placeholder={t("Select...")}
                                isSearchable={true}
                                isClearable={true}
                                className="w-100"
                                value={
                                  plateTypeOptions.find(
                                    (o) => o.value === searchRequest.plateTypeId
                                  ) ?? null
                                }
                                onChange={(opt: any) =>
                                  setSearchRequest((prev) => ({
                                    ...prev,
                                    plateTypeId: opt?.value ?? "",
                                  }))
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <input
                                id="PlateSetupSearchAccessions"
                                type="text"
                                name="noOfAccessionId"
                                className="form-control bg-white min-w-100px w-100 rounded-2 fs-8 h-30px"
                                placeholder={t("Search ...")}
                                value={searchRequest.noOfAccessionId}
                                onChange={onSearchQueryChange}
                                onKeyDown={handleSearchKeyPress}
                              />
                            </TableCell>
                          </TableRow>
                          <TableRow className="h-30px">
                            <TableCell
                              className="min-w-50px"
                              style={{ width: "49px" }}
                            />
                            <TableCell
                              className="min-w-50px"
                              style={{ width: "49px" }}
                            >
                              {t("Actions")}
                            </TableCell>
                            {/* <TableCell
                            className="min-w-100px w-100px"
                            sx={{ width: "max-content" }}
                          >
                            {renderSortHeader("Test Type", "runName")}
                          </TableCell> */}
                            <TableCell
                              className="min-w-150px w-150px"
                              sx={{ width: "max-content" }}
                            >
                              {renderSortHeader("Panel", "panelId")}
                            </TableCell>
                            <TableCell
                              className="min-w-100px w-100px"
                              sx={{ width: "max-content" }}
                            >
                              {renderSortHeader("Template", "outputTemplate")}
                            </TableCell>
                            <TableCell
                              className="min-w-100px w-100px"
                              sx={{ width: "max-content" }}
                            >
                              {renderSortHeader(
                                "Mapping Type",
                                "mappingTypeId"
                              )}
                            </TableCell>
                            <TableCell
                              className="min-w-100px w-100px"
                              sx={{ width: "max-content" }}
                            >
                              {renderSortHeader("Plate Type", "plateTypeId")}
                            </TableCell>
                            <TableCell
                              className="min-w-150px w-150px"
                              sx={{ width: "max-content" }}
                            >
                              {renderSortHeader(
                                "No. of Accessions in 1 plate",
                                "noOfAccessionId"
                              )}
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody
                          sx={{
                            "& .MuiTableCell-body": {
                              padding: "6px 16px !important",
                              fontWeight: 400,
                              fontSize: "14px !important",
                              lineHeight: "18px",
                              borderBottom: 0,
                              color: "#3F4254",
                              verticalAlign: "middle",
                            },
                          }}
                        >
                          {rows.map((row) => (
                            <React.Fragment key={row.id}>
                              <TableRow className="h-30px">
                                <TableCell>
                                  {!row.rowStatus ? (
                                    expandedId === row.id ? (
                                      <button
                                        type="button"
                                        id={`PlateSetupCloseExpand_${row.id}`}
                                        className="btn btn-icon btn-icon-light btn-sm fw-bold btn-table-expend-row rounded h-20px w-20px min-h-20px"
                                        onClick={() => setExpandedId(null)}
                                        title={t("Collapse")}
                                      >
                                        <i className="bi bi-dash-lg" />
                                      </button>
                                    ) : (
                                      <button
                                        type="button"
                                        id={`PlateSetupOpenExpand_${row.id}`}
                                        className="btn btn-icon btn-icon-light btn-sm fw-bold btn-primary rounded h-20px w-20px min-h-20px"
                                        onClick={() => {
                                          setExpandedId(row.id);
                                          if (row.panelId?.trim()) {
                                            fetchTestControlOptions(
                                              row.panelId
                                            );
                                          }
                                        }}
                                        title={t("Expand")}
                                      >
                                        <i className="bi bi-plus-lg" />
                                      </button>
                                    )
                                  ) : null}
                                </TableCell>
                                <TableCell>
                                  <div className="d-flex justify-content-center rotatebtnn">
                                    {row.rowStatus ? (
                                      <div className="d-flex gap-2">
                                        <PermissionComponent
                                          moduleName={ID_LIS_MODULE_NAME}
                                          pageName={PLATE_SETUP_PERMISSION_PAGE}
                                          permissionIdentifier={
                                            CREATE_PLATE_TEMPLATES_PERMISSION
                                          }
                                        >
                                          <button
                                            type="button"
                                            id={`PlateSetupSave_${row.id}`}
                                            onClick={() => confirmNewRow(row)}
                                            className="btn btn-icon btn-sm fw-bold btn-table-save btn-icon-light h-32px w-32px fas-icon-20px"
                                          >
                                            <DoneIcon />
                                          </button>
                                        </PermissionComponent>
                                        <button
                                          type="button"
                                          id={`PlateSetupCancel_${row.id}`}
                                          onClick={() => cancelNewRow(row.id)}
                                          className="btn btn-icon btn-sm fw-bold btn-table-cancel btn-icon-light h-32px w-32px fas-icon-20px"
                                        >
                                          <CrossIcon />
                                        </button>
                                      </div>
                                    ) : (
                                      <DropdownButton
                                        className="p-0 del-before btn btn-light-info btn-active-info btn-sm btn-action table-action-btn"
                                        id={`PlateSetupMenu_${row.id}`}
                                        drop="end"
                                        title={
                                          <i className="bi bi-three-dots-vertical p-0"></i>
                                        }
                                      >
                                        <PermissionComponent
                                          moduleName={ID_LIS_MODULE_NAME}
                                          pageName={PLATE_SETUP_PERMISSION_PAGE}
                                          permissionIdentifier={
                                            CREATE_PLATE_TEMPLATES_PERMISSION
                                          }
                                        >
                                          <Dropdown.Item
                                            className="menu-item p-0"
                                            disabled={isDeletingId === row.id}
                                            onClick={() =>
                                              setDeleteConfirmId(row.id)
                                            }
                                          >
                                            <span className="menu-link text-dark">
                                              <i className="fa fa-trash text-danger mr-2 w-20px"></i>
                                              {isDeletingId === row.id
                                                ? t("Deleting...")
                                                : t("Delete")}
                                            </span>
                                          </Dropdown.Item>
                                        </PermissionComponent>
                                      </DropdownButton>
                                    )}
                                  </div>
                                </TableCell>
                                {/* <TableCell>
                                {row.rowStatus ? (
                                  <input
                                    id={`PlateSetupTestType_${row.id}`}
                                    type="text"
                                    className="form-control bg-white mb-1 h-30px rounded-2 fs-8 w-100"
                                    style={inputStyles}
                                    value={row.testType}
                                    onChange={(e) =>
                                      updateRow(row.id, {
                                        testType: e.target.value,
                                      })
                                    }
                                  />
                                ) : (
                                  <span>{row.testType}</span>
                                )}
                              </TableCell> */}
                                <TableCell>
                                  {row.rowStatus ? (
                                    <div className="d-flex justify-content-between w-100">
                                      <div className="w-100">
                                        <Select<LookupOption, true>
                                          inputId={`PlateSetupPanel_${row.id}`}
                                          theme={(theme) => styles(theme)}
                                          options={panelOptions}
                                          isMulti
                                          closeMenuOnSelect={false}
                                          styles={fullWidthSelectStyles}
                                          menuPortalTarget={document.body}
                                          placeholder="--- Select ---"
                                          isSearchable={true}
                                          className="w-100"
                                          value={panelOptions.filter((option) =>
                                            parsePanelIds(row.panelId).includes(
                                              option.value
                                            )
                                          )}
                                          onChange={(selected) => {
                                            const selectedPanels =
                                              selected ?? [];
                                            updateRow(row.id, {
                                              panelId: selectedPanels.length
                                                ? serializePanelIds(
                                                    selectedPanels.map(
                                                      (option) => option.value
                                                    )
                                                  )
                                                : null,
                                              panel: selectedPanels
                                                .map((option) => option.label)
                                                .join(", "),
                                            });
                                          }}
                                        />
                                      </div>
                                    </div>
                                  ) : (
                                    <span>{row.panel}</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {row.rowStatus ? (
                                    <input
                                      id={`PlateSetupTemplate_${row.id}`}
                                      type="text"
                                      className="form-control bg-white mb-1 h-30px rounded-2 fs-8 w-100"
                                      style={inputStyles}
                                      value={row.template}
                                      onChange={(e) =>
                                        updateRow(row.id, {
                                          template: e.target.value,
                                        })
                                      }
                                    />
                                  ) : (
                                    <span>{row.template}</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {row.rowStatus ? (
                                    <div className="d-flex justify-content-between w-100">
                                      <div className="w-100">
                                        <Select
                                          inputId={`PlateSetupMapping_${row.id}`}
                                          theme={(theme) => styles(theme)}
                                          options={mappingTypeOptions}
                                          styles={fullWidthSelectStyles}
                                          menuPortalTarget={document.body}
                                          placeholder="--- Select ---"
                                          isSearchable={true}
                                          className="w-100"
                                          value={
                                            mappingTypeOptions.find(
                                              (o) =>
                                                o.value ===
                                                String(row.mappingTypeId ?? "")
                                            ) ?? null
                                          }
                                          onChange={(opt: any) =>
                                            updateRow(row.id, {
                                              mappingTypeId: opt?.value ?? null,
                                              mappingType: opt?.label ?? "",
                                            })
                                          }
                                        />
                                      </div>
                                    </div>
                                  ) : (
                                    <span>{row.mappingType}</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {row.rowStatus ? (
                                    <div className="d-flex justify-content-between w-100">
                                      <div className="w-100">
                                        <Select
                                          inputId={`PlateSetupPlateType_${row.id}`}
                                          theme={(theme) => styles(theme)}
                                          options={plateTypeOptions}
                                          styles={fullWidthSelectStyles}
                                          menuPortalTarget={document.body}
                                          placeholder="--- Select ---"
                                          isSearchable={true}
                                          className="w-100"
                                          value={
                                            plateTypeOptions.find(
                                              (o) =>
                                                o.value ===
                                                String(row.plateTypeId)
                                            ) ?? null
                                          }
                                          onChange={(opt: any) =>
                                            updateRow(row.id, {
                                              plateTypeId: opt?.value ?? "1",
                                              plateType:
                                                opt?.label ?? "96 Well",
                                            })
                                          }
                                        />
                                      </div>
                                    </div>
                                  ) : (
                                    <span>{row.plateType}</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {row.rowStatus ? (
                                    <div className="d-flex justify-content-between w-100">
                                      <div className="w-100">
                                        <Select
                                          inputId={`PlateSetupAccessions_${row.id}`}
                                          theme={(theme) => styles(theme)}
                                          options={accessionSelectOptions(
                                            row.plateTypeId,
                                            row.plateType
                                          )}
                                          styles={fullWidthSelectStyles}
                                          menuPortalTarget={document.body}
                                          placeholder="--- Select ---"
                                          isSearchable={true}
                                          className="w-100"
                                          value={
                                            accessionSelectOptions(
                                              row.plateTypeId,
                                              row.plateType
                                            ).find(
                                              (o) =>
                                                o.value ===
                                                row.accessionsPerPlate
                                            ) ?? null
                                          }
                                          onChange={(opt: any) => {
                                            const opts = accessionsForPlateType(
                                              row.plateTypeId,
                                              row.plateType
                                            );
                                            updateRow(row.id, {
                                              accessionsPerPlate:
                                                opt?.value ?? opts[0],
                                            });
                                          }}
                                        />
                                      </div>
                                    </div>
                                  ) : (
                                    <span>{row.accessionsPerPlate}</span>
                                  )}
                                </TableCell>
                              </TableRow>
                              {!row.rowStatus && expandedId === row.id && (
                                <TableRow>
                                  <TableCell
                                    colSpan={8}
                                    className="p-0 border-0"
                                  >
                                    <div
                                      className="p-4"
                                      style={{
                                        background: "#fafbfc",
                                        borderTop: "1px solid #eff2f5",
                                      }}
                                    >
                                      <div className="d-flex justify-content-between align-items-center mb-3">
                                        <span
                                          className="fw-semibold"
                                          style={{
                                            color: "#070910",
                                            fontFamily: "Poppins",
                                            fontSize: "14px",
                                          }}
                                        >
                                          {isPlateDetailsLoading &&
                                          row.slots.length === 0
                                            ? t("Loading...")
                                            : `${row.slots.length} ${t("entries")}`}
                                        </span>
                                        <PermissionComponent
                                          moduleName={ID_LIS_MODULE_NAME}
                                          pageName={PLATE_SETUP_PERMISSION_PAGE}
                                          permissionIdentifier={
                                            CREATE_PLATE_TEMPLATES_PERMISSION
                                          }
                                        >
                                          <button
                                            type="button"
                                            id={`PlateSetupSaveAssignments_${row.id}`}
                                            className="btn btn-primary btn-sm fw-bold px-6"
                                            disabled={
                                              !!isSavingAssignments[row.id] ||
                                              (isPlateDetailsLoading &&
                                                row.slots.length === 0) ||
                                              isPlateDetailsError
                                            }
                                            onClick={() => saveAssignments(row)}
                                          >
                                            {isSavingAssignments[row.id]
                                              ? t("Saving...")
                                              : t("Save Assignments")}
                                          </button>
                                        </PermissionComponent>
                                      </div>
                                      {isPlateDetailsError &&
                                        row.slots.length === 0 && (
                                          <div className="alert alert-danger d-flex justify-content-between align-items-center py-2 mb-3">
                                            <span>
                                              {t(
                                                (plateDetailsError as any)
                                                  ?.message ||
                                                  "Unable to load plate setup details"
                                              )}
                                            </span>
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-light-danger"
                                              onClick={() =>
                                                refetchPlateDetails()
                                              }
                                            >
                                              {t("Retry")}
                                            </button>
                                          </div>
                                        )}
                                      <div className="table_bordered overflow-hidden rounded-3">
                                        <table className="table table-bordered table-sticky-header table-vertical-center border-0 mb-0 plate-setup-expand-subtable">
                                          <thead>
                                            <tr>
                                              <th
                                                scope="col"
                                                style={{
                                                  fontWeight: 600,
                                                  color: "#070910",
                                                  fontFamily: "Poppins",
                                                  fontSize: "14px",
                                                  borderBottom: 0,
                                                  backgroundColor: "#FAFAD2",
                                                  boxShadow: "none",
                                                }}
                                              >
                                                {t("Well")}
                                              </th>
                                              <th
                                                scope="col"
                                                style={{
                                                  fontWeight: 600,
                                                  color: "#070910",
                                                  fontFamily: "Poppins",
                                                  fontSize: "14px",
                                                  borderBottom: 0,
                                                  backgroundColor: "#FAFAD2",
                                                  boxShadow: "none",
                                                }}
                                              >
                                                {t("Accession No")}
                                              </th>
                                              <th
                                                scope="col"
                                                style={{
                                                  fontWeight: 600,
                                                  color: "#070910",
                                                  fontFamily: "Poppins",
                                                  fontSize: "14px",
                                                  borderBottom: 0,
                                                  backgroundColor: "#FAFAD2",
                                                  boxShadow: "none",
                                                }}
                                              >
                                                {t("Target")}
                                              </th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {isPlateDetailsLoading &&
                                              row.slots.length === 0 && (
                                                <tr>
                                                  <td
                                                    colSpan={3}
                                                    className="text-center py-4"
                                                  >
                                                    {t("Loading...")}
                                                  </td>
                                                </tr>
                                              )}
                                            {row.slots.map((slot, si) => (
                                              <tr key={slot.wellPosition}>
                                                <td
                                                  style={{
                                                    color: "#3F4254",
                                                    fontSize: "14px",
                                                    verticalAlign: "middle",
                                                    boxShadow: "none",
                                                  }}
                                                >
                                                  {slot.wellName ??
                                                    slot.wellPosition}
                                                </td>
                                                <td
                                                  style={{
                                                    color: "#3F4254",
                                                    fontSize: "14px",
                                                    verticalAlign: "middle",
                                                    boxShadow: "none",
                                                  }}
                                                >
                                                  <span
                                                    id={`PlateSetupAccession_${row.id}_${si}`}
                                                  >
                                                    {slot.accessionPlaceholder ??
                                                      (String(
                                                        row.plateTypeId
                                                      ) === "1"
                                                        ? getAccessionPlaceholderForWell96(
                                                            slot.wellPosition,
                                                            row.accessionsPerPlate
                                                          )
                                                        : "-")}
                                                  </span>
                                                </td>
                                                <td
                                                  style={{
                                                    verticalAlign: "middle",
                                                    boxShadow: "none",
                                                  }}
                                                >
                                                  <div className="d-flex justify-content-between w-100">
                                                    <div className="w-100">
                                                      {(() => {
                                                        const cacheKey = String(
                                                          row.panelId ?? ""
                                                        );
                                                        const flatOptions: PlateAssignOption[] =
                                                          (cacheKey &&
                                                            testControlCache[
                                                              cacheKey
                                                            ]) ||
                                                          [];
                                                        const isLoadingOptions =
                                                          cacheKey
                                                            ? (testControlLoading[
                                                                cacheKey
                                                              ] ?? false)
                                                            : false;
                                                        const groupedOptions: PlateAssignGroup[] =
                                                          groupTestControlOptions(
                                                            flatOptions
                                                          );
                                                        return (
                                                          <Select<
                                                            PlateAssignOption,
                                                            true,
                                                            PlateAssignGroup
                                                          >
                                                            inputId={`PlateSetupRack_${row.id}_${slot.wellPosition}`}
                                                            isMulti
                                                            theme={(theme) =>
                                                              styles(theme)
                                                            }
                                                            menuPortalTarget={
                                                              document.body
                                                            }
                                                            options={
                                                              groupedOptions
                                                            }
                                                            // getOptionValue={(o) => o.value}
                                                            getOptionValue={(
                                                              o
                                                            ) =>
                                                              `${o.isControl ? "C" : "T"}-${o.value}`
                                                            }
                                                            getOptionLabel={(
                                                              o
                                                            ) => o.label}
                                                            formatOptionLabel={(
                                                              o
                                                            ) => (
                                                              <div className="d-flex align-items-center gap-2">
                                                                <span
                                                                  className={`badge badge-sm ${
                                                                    o.isControl
                                                                      ? "badge-light-warning"
                                                                      : "badge-light-primary"
                                                                  }`}
                                                                  style={{
                                                                    fontSize:
                                                                      "10px",
                                                                    minWidth:
                                                                      "52px",
                                                                  }}
                                                                >
                                                                  {o.isControl
                                                                    ? "Control"
                                                                    : "Test"}
                                                                </span>
                                                                <span>
                                                                  {o.label}
                                                                </span>
                                                              </div>
                                                            )}
                                                            isLoading={
                                                              isLoadingOptions
                                                            }
                                                            loadingMessage={() =>
                                                              t("Loading...")
                                                            }
                                                            noOptionsMessage={() =>
                                                              !row.panelId?.trim()
                                                                ? t(
                                                                    "No panels selected"
                                                                  )
                                                                : t(
                                                                    "No options"
                                                                  )
                                                            }
                                                            styles={
                                                              fullWidthSelectStyles
                                                            }
                                                            placeholder="--- Select ---"
                                                            isSearchable={true}
                                                            className="w-100"
                                                            value={selectValuesFromAssignJson(
                                                              slot.assignTestJson,
                                                              flatOptions
                                                            )}
                                                            onChange={(
                                                              selected
                                                            ) => {
                                                              updateSlot(
                                                                row.id,
                                                                si,
                                                                {
                                                                  assignTestJson:
                                                                    assignJsonFromSelectOptions(
                                                                      selected ??
                                                                        []
                                                                    ),
                                                                }
                                                              );
                                                            }}
                                                          />
                                                        );
                                                      })()}
                                                    </div>
                                                  </div>
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          ))}
                          {isSetupsLoading && rows.length === 0 && (
                            <TableRow>
                              <TableCell
                                colSpan={8}
                                className="text-center py-4"
                              >
                                {t("Loading...")}
                              </TableCell>
                            </TableRow>
                          )}
                          {!isSetupsLoading && rows.length === 0 && (
                            <TableRow>
                              <TableCell
                                colSpan={8}
                                className="text-center py-4"
                              >
                                {t("No records found")}
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </div>
                </Box>

                {total > 0 && (
                  <CustomPagination
                    curPage={curPage}
                    nextPage={nextPage}
                    pageNumbers={pageNumbers}
                    pageSize={pageSize}
                    prevPage={prevPage}
                    showPage={showPage}
                    total={total}
                    totalPages={totalPages}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ── Delete confirmation modal ───────────────────────────────────── */}
      <Modal
        show={deleteConfirmId !== null}
        onHide={() => setDeleteConfirmId(null)}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton className="bg-light-primary m-0 p-5">
          <h4>{t("Delete Record")}</h4>
        </Modal.Header>
        <Modal.Body>
          {t("Are you sure you want to delete this record ?")}
        </Modal.Body>
        <Modal.Footer className="p-0">
          <button
            id="PlateSetupDeleteModalCancel"
            type="button"
            className="btn btn-secondary"
            onClick={() => setDeleteConfirmId(null)}
          >
            {t("Cancel")}
          </button>
          <button
            id="PlateSetupDeleteModalConfirm"
            type="button"
            className="btn btn-danger m-2"
            onClick={handleDeleteConfirm}
          >
            {t("Delete")}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PlateSetup;
